// Tiny wrapper around the Web Speech API for rehearsal-mark announcements.
//
// Chrome's speechSynthesis has long-standing wedging behavior — after a
// cancel(), after page idle, or after a stop/restart cycle, the synth can
// silently get stuck in a paused state where new utterances queue but never
// play. The functions here defend against that by:
//   1) Priming the voice list once (getVoices()) so the first real speak()
//      isn't delayed waiting on async voice load.
//   2) Spinning up the engine via warmUpSpeech() — callers fire it from
//      synchronous user-gesture handlers (toggle click, play click) to
//      ensure the synth has loaded a voice and started its audio pipeline
//      before any real announcement is due.
//   3) Calling cancel() + resume() before every speak() to force the synth
//      out of a wedged state — except within a short window after a warmup
//      so the warmup chain isn't killed before it can finish initializing.
//   4) Exposing a keepalive that callers can run on an interval to nudge
//      the synth periodically while a playback session is active.

let supportedCache = null;
let voicesPrimed = false;
let lastWarmupAt = 0;
const WARMUP_PROTECT_MS = 500;

export function isSpeechSupported() {
  if (supportedCache === null) {
    supportedCache =
      typeof window !== "undefined" &&
      "speechSynthesis" in window &&
      typeof window.SpeechSynthesisUtterance !== "undefined";
  }
  return supportedCache;
}

function primeVoices() {
  if (voicesPrimed || !isSpeechSupported()) return;
  voicesPrimed = true;
  try {
    window.speechSynthesis.getVoices();
  } catch (e) {}
}

// Fire a few near-silent utterances to fully spin up the synth — load the
// active voice and initialize the system speech service. Without this, the
// first real speak() in a session can take many seconds (or many failed
// attempts) before audio actually appears.
//
// Three non-obvious details:
//   - The text is audible-but-imperceptible (very low volume, fast rate,
//     short word). Browsers optimize away utterances with volume 0 or empty
//     text and never actually exercise the engine.
//   - We queue several utterances per call. A single short one can also be
//     optimized away or processed too quickly to fully initialize the
//     engine; chaining a few forces real work.
//   - Callers should invoke this from a synchronous user-gesture handler
//     (click/touch), not a useEffect — Chrome and Safari gate speech behind
//     recent user activation, and by the time effects run the gesture window
//     may have closed. Each call is also a fresh refresh of that activation,
//     so re-warming on every gesture is intentional, not wasteful.
export function warmUpSpeech() {
  if (!isSpeechSupported()) return;
  primeVoices();
  try {
    const synth = window.speechSynthesis;
    if (synth.paused) synth.resume();
    for (let i = 0; i < 3; i++) {
      const utt = new window.SpeechSynthesisUtterance("warm");
      utt.volume = 0.01;
      utt.rate = 4;
      synth.speak(utt);
    }
    lastWarmupAt = Date.now();
  } catch (e) {}
}

export function speak(text) {
  if (!isSpeechSupported() || !text) return;
  primeVoices();
  try {
    const synth = window.speechSynthesis;
    // Force-reset the synth state. The cancel is the bug-guard — without it,
    // the synth can stick in a paused state across stop/restart where new
    // utterances queue but never play. The exception: if a warmup just ran,
    // skip the cancel so the warmup chain has time to actually initialize
    // the engine. Killing warmups too early leaves the synth cold for the
    // first several real announcements.
    if (Date.now() - lastWarmupAt > WARMUP_PROTECT_MS) {
      synth.cancel();
    }
    synth.resume();
    // Lowercase the input — many TTS voices (notably macOS) prepend "Capital"
    // before any uppercase letter, so "A" reads as "Capital A". Lowercasing
    // suppresses that without changing how digits or words sound.
    const utt = new window.SpeechSynthesisUtterance(String(text).toLowerCase());
    utt.rate = 1.1;
    synth.speak(utt);
  } catch (e) {
    // Speech is non-critical; never let it break playback.
  }
}

export function cancelSpeech() {
  if (!isSpeechSupported()) return;
  try {
    window.speechSynthesis.cancel();
  } catch (e) {}
}

// Periodic nudge to prevent the synth from going dormant. Chrome will stop
// processing the queue after ~15 seconds of inactivity in some states; a
// resume() call (even when not paused) keeps it warm.
export function pingSpeechKeepalive() {
  if (!isSpeechSupported()) return;
  try {
    const synth = window.speechSynthesis;
    if (!synth.speaking) {
      synth.resume();
    }
  } catch (e) {}
}
